import { formatPrice } from '../lib/api';

/**
 * Small pieces shared between HistoryTab (what was pushed) and PendingChanges
 * (what's staged to be). Both are "old → new, on this channel" tables, so the
 * per-cell rendering is one set of components rather than two copies that
 * would drift the moment one of them got a formatting fix the other didn't.
 */

/** The channel, coloured by which storefront it belongs to. */
export function ChannelTag({ name, type }: { name: string; type: string }) {
  const site = type === 'drupal_uc' ? 'mlf' : type === 'drupal_commerce' ? 'mff' : 'market';
  return <span className={`pill pill-${site}`}>{name}</span>;
}

/** One field's value, formatted so a price reads as money — shared by Change below and PendingChanges' own Current/New columns. */
export function formatChangeValue(field: string, value: string | null, currency: string) {
  if (value === null || value === '') return <span className="muted">empty</span>;
  if (field !== 'price') return value;
  const minor = Number(value);
  return Number.isFinite(minor) ? formatPrice(minor, currency) : value;
}

/** Old → new, formatted for the field so a price reads as money. */
export function Change({
  field,
  oldValue,
  newValue,
  currency,
  /**
   * Set only on a "price" entry that also carried a business price — what it
   * was set to. Null everywhere else, including every non-Amazon channel and
   * a staged (not yet pushed) row, which never carries one.
   */
  businessNewValue,
}: {
  field: string;
  oldValue: string | null;
  newValue: string | null;
  currency: string;
  businessNewValue?: number | null;
}) {
  return (
    <span className={field === 'price' ? 'nowrap' : undefined}>
      <span className="muted small">{field}</span>{' '}
      <span className="old-value">{formatChangeValue(field, oldValue, currency)}</span>
      <span className="muted"> → </span>
      <strong>{formatChangeValue(field, newValue, currency)}</strong>
      {businessNewValue != null && (
        <div className="muted small">business price also set to {formatPrice(businessNewValue, currency)}</div>
      )}
    </span>
  );
}
